import { MAX_NAME_PART_BYTES } from "odyssey-shared";
import type { FamilyFetch, FamilyRead } from "./family-client.ts";

/**
 * The page's read of the family the Moderation window is on (ADR-056's amendment of 2026-10-06, decisions 3 and 13),
 * owned by the page on the desk's pattern: one request when a family is chosen, from the list or the form's field,
 * and again after an action of the GM's own is written. An answer to a family the window has since left is dropped.
 * Nothing is asked on a timer: the Online players list refreshes, a family's read does not.
 */

/**
 * Where the read stands: nothing chosen; reading; read; nobody holds that name; or a failure, a limit, a session or
 * a role that ended.
 */
export type FamilyState = "none" | "reading" | "ok" | "missing" | "failed" | "rate" | "session" | "forbidden";

/**
 * What the window shows of the family: its name as chosen, the read (kept while the same family is read again) and
 * where the read stands.
 */
export interface FamilySnapshot {
  readonly name: string | null;
  readonly read: FamilyRead | null;
  readonly state: FamilyState;
}

const FAMILY = new RegExp(`^[A-Za-z0-9_-]{1,${MAX_NAME_PART_BYTES}}$`);
const NONE: FamilySnapshot = Object.freeze({ name: null, read: null, state: "none" });

/**
 * The model.
 */
export class FamilyModel {
  private readonly listeners = new Set<() => void>();
  private snapshot: FamilySnapshot = NONE;
  private asked = 0;
  private flight: Promise<void> = Promise.resolve();

  /**
   * @param fetch Reads one family.
   */
  public constructor(private readonly fetch: FamilyFetch) {}

  /**
   * Hears that the snapshot changed. Works unbound, as React calls it.
   *
   * @param listener Called on each change.
   * @returns Stops hearing.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * What the window shows now. Works unbound, as React calls it.
   *
   * @returns The snapshot, the same object until something changes.
   */
  public readonly getSnapshot = (): FamilySnapshot => this.snapshot;

  /**
   * Chooses a family, or none, and reads it. A name no family can hold is chosen and read as nobody's.
   *
   * @param name The family as named, or null.
   */
  public readonly choose = (name: string | null): void => {
    const trimmed = name?.trim() ?? "";
    if (trimmed === "") {
      this.asked += 1;
      this.set(NONE);
      return;
    }
    const same = this.snapshot.name !== null && this.snapshot.name.toLowerCase() === trimmed.toLowerCase();
    this.read(trimmed, same ? this.snapshot.read : null);
  };

  /**
   * Reads the chosen family again, as after an action of the GM's own was written.
   */
  public readonly refresh = (): void => {
    if (this.snapshot.name !== null) {
      this.read(this.snapshot.name, this.snapshot.read);
    }
  };

  /**
   * Settles once the last read asked has been answered.
   *
   * @returns A promise that settles then.
   */
  public idle(): Promise<void> {
    return this.flight;
  }

  /**
   * Reads a family, keeping what was read of it while the request is out.
   *
   * @param name The family.
   * @param kept The read to show meanwhile, or null.
   */
  private read(name: string, kept: FamilyRead | null): void {
    this.asked += 1;
    const asked = this.asked;
    if (!FAMILY.test(name)) {
      this.set({ name, read: null, state: "missing" });
      return;
    }
    this.set({ name, read: kept, state: "reading" });
    this.flight = this.fetch(name).then((reply) => {
      if (asked !== this.asked) {
        return;
      }
      if (reply.kind === "ok") {
        this.set({ name: reply.family.family, read: reply.family, state: "ok" });
        return;
      }
      this.set({ name, read: reply.kind === "missing" || reply.kind === "session" || reply.kind === "forbidden" ? null : kept, state: reply.kind === "unreadable" ? "failed" : reply.kind });
    });
  }

  /**
   * Replaces the snapshot and tells the listeners.
   *
   * @param next The snapshot.
   */
  private set(next: FamilySnapshot): void {
    this.snapshot = next;
    for (const listener of this.listeners) {
      listener();
    }
  }
}
